import { useEffect, useState } from "react"

export default function useAgendaNavigationContext({
  location,
  navigate,
  clinicId,
  treatments,
  calendarRef,
  setActiveClinicId,
  setDate,
  setNewAppt,
  setDoctorSuggestion,
  setAlternatives,
  setPatientQuery,
  setQuickPatient,
  setShowNewModal,
  setSelectedAppointment,
  loadHeldSlots,
  loadAgenda,
  handleCalendarEventClick,
  getTodayLocal,
  formatDateTimeLocal
}) {
  const [pendingOpenAppointmentId, setPendingOpenAppointmentId] = useState("")
  const [pendingOpenAppointmentDate, setPendingOpenAppointmentDate] = useState("")
  const [pendingOpenAppointmentClinicId, setPendingOpenAppointmentClinicId] = useState("")
  const [pendingFocusAction, setPendingFocusAction] = useState("")

  const consumeOpenAppointmentContext = () => {
    try {
      const raw = localStorage.getItem("odontoflow_open_appointment")
      if (!raw) return null

      localStorage.removeItem("odontoflow_open_appointment")
      return JSON.parse(raw)
    } catch {
      localStorage.removeItem("odontoflow_open_appointment")
      return null
    }
  }

  const consumeNewAppointmentContext = () => {
    try {
      const raw = localStorage.getItem("odontoflow_new_appointment_context")
      if (!raw) return null

      localStorage.removeItem("odontoflow_new_appointment_context")
      return JSON.parse(raw)
    } catch {
      localStorage.removeItem("odontoflow_new_appointment_context")
      return null
    }
  }

  useEffect(() => {
    const context = consumeOpenAppointmentContext()
    if (!context?.appointmentId) return

    const targetClinicId =
      context.clinicId && context.clinicId !== "ALL"
        ? String(context.clinicId)
        : ""

    const targetDate =
      context.date && typeof context.date === "string"
        ? context.date
        : ""

    if (!targetClinicId) {
      console.warn("OPEN_APPOINTMENT_ABORTED: clinicId inválido", context)
      return
    }

    const bootstrapOpenAppointment = async () => {
      localStorage.setItem("odontoflow_clinic_id", targetClinicId)

      setPendingOpenAppointmentId(String(context.appointmentId))
      setPendingOpenAppointmentDate(targetDate)
      setPendingOpenAppointmentClinicId(targetClinicId)

      if (context.focusAction) {
        setPendingFocusAction(context.focusAction)
      }

      setActiveClinicId(targetClinicId)

      if (targetDate) {
        setDate(targetDate)
      }

      const effectiveDate = targetDate || getTodayLocal()

      await loadHeldSlots(targetClinicId, effectiveDate)
      await loadAgenda(targetClinicId, effectiveDate)

      setTimeout(() => {
        const api = calendarRef.current?.getApi?.()
        if (api && targetDate) {
          api.gotoDate(targetDate)
        }
      }, 50)
    }

    bootstrapOpenAppointment()
  }, [
    calendarRef,
    loadAgenda,
    loadHeldSlots,
    setActiveClinicId,
    setDate,
    getTodayLocal
  ])

  useEffect(() => {
    const ctx = location.state
    if (!ctx?.autoOpenNewAppointment) return

    const treatment = ctx.treatmentFromPlan

    const suggestedStart = (() => {
      const now = new Date()
      now.setMinutes(0)
      now.setSeconds(0)
      now.setMilliseconds(0)

      if (now.getHours() >= 18) {
        const tomorrow = new Date()
        tomorrow.setDate(tomorrow.getDate() + 1)
        tomorrow.setHours(9, 0, 0, 0)
        return formatDateTimeLocal(tomorrow)
      }

      if (now.getHours() < 9) {
        now.setHours(9, 0, 0, 0)
        return formatDateTimeLocal(now)
      }

      now.setHours(now.getHours() + 1)
      return formatDateTimeLocal(now)
    })()

    setNewAppt((prev) => ({
      ...prev,
      patientId: ctx.patientId || "",
      treatmentId: treatment?.treatmentId || "",
      doctorId: treatment?.assignedDoctorId || treatment?.suggestedDoctorId || "",
      start: suggestedStart,
      duration: treatment?.durationMinutes || 30,
      treatmentPlanItemId: treatment?.id || "",
      planTitle: treatment?.planTitle || ctx.planId || ""
    }))

    setDoctorSuggestion(null)
    setAlternatives([])
    setPatientQuery("")
    setQuickPatient({
      firstName: "",
      lastName: "",
      phone: "",
      email: ""
    })

    setShowNewModal(true)

    navigate(location.pathname, { replace: true })
  }, [
    formatDateTimeLocal,
    location,
    navigate,
    setAlternatives,
    setDoctorSuggestion,
    setNewAppt,
    setPatientQuery,
    setQuickPatient,
    setShowNewModal
  ])

  useEffect(() => {
    const context = consumeNewAppointmentContext()
    if (!context?.patientId) return

    const targetClinicId =
      context.clinicId && context.clinicId !== "ALL"
        ? context.clinicId
        : clinicId && clinicId !== "ALL"
          ? clinicId
          : ""

    const openModalWithPatient = async () => {
      if (targetClinicId && targetClinicId !== clinicId) {
        setActiveClinicId(targetClinicId)
      }

      const selectedTreatmentFromContext = context.treatmentId
        ? treatments.find((t) => t.id === context.treatmentId)
        : null

      setNewAppt((prev) => ({
        ...prev,
        patientId: context.patientId,
        treatmentId: context.treatmentId || "",
        doctorId:
          context.assignedDoctorId ||
          context.suggestedDoctorId ||
          "",
        start: "",
        duration:
          selectedTreatmentFromContext?.durationMinutes ||
          prev.duration ||
          30,
        treatmentPlanItemId: context.treatmentPlanItemId || "",
        planTitle: context.treatmentPlanId || ""
      }))

      setDoctorSuggestion(null)
      setAlternatives([])
      setPatientQuery("")
      setQuickPatient({
        firstName: "",
        lastName: "",
        phone: "",
        email: ""
      })

      setShowNewModal(true)
    }

    openModalWithPatient()
  }, [
    clinicId,
    setActiveClinicId,
    setAlternatives,
    setDoctorSuggestion,
    setNewAppt,
    setPatientQuery,
    setQuickPatient,
    setShowNewModal,
    treatments
  ])

  const tryOpenPendingAppointment = async (events) => {
    if (!pendingOpenAppointmentId) return
    if (!events.length) return

    if (
      pendingOpenAppointmentClinicId &&
      clinicId &&
      String(pendingOpenAppointmentClinicId) !== String(clinicId)
    ) {
      return
    }

    const event = events.find(
      (e) => String(e.id) === String(pendingOpenAppointmentId)
    )

    if (!event) return

    if (pendingOpenAppointmentDate) {
      const api = calendarRef.current?.getApi?.()
      if (api) {
        api.gotoDate(pendingOpenAppointmentDate)
      }
    }

    const fakeInfo = {
      event: {
        id: event.id,
        title: event.title,
        start: event.start,
        end: event.end,
        extendedProps: event.extendedProps || {}
      }
    }

    await handleCalendarEventClick(fakeInfo)

    setPendingOpenAppointmentId("")
    setPendingOpenAppointmentDate("")
    setPendingOpenAppointmentClinicId("")
  }

  useEffect(() => {
    if (!pendingFocusAction) return
    if (!setSelectedAppointment) return

    if (pendingFocusAction === "PAYMENT") {
      console.log("Enfocar sección de pago")
    }

    setPendingFocusAction("")
  }, [pendingFocusAction, setSelectedAppointment])

  return {
    pendingOpenAppointmentId,
    pendingOpenAppointmentDate,
    pendingOpenAppointmentClinicId,
    pendingFocusAction,
    setPendingOpenAppointmentId,
    setPendingOpenAppointmentDate,
    setPendingOpenAppointmentClinicId,
    setPendingFocusAction,
    tryOpenPendingAppointment
  }
}